import type { BatchPreviewTableColumn } from "./batch-preview-table.type";
import type {
  BatchPreviewItem,
  BatchPreviewModelKey,
} from "../batch-preview.type";

import { InfoCircleOutlined } from "@ant-design/icons";
import { Flex, Tooltip } from "antd";
import uniqBy from "lodash/uniqBy";
import { useTranslation } from "react-i18next";

import { useReferentialStates } from "@/entities/referential/referential-states";
import { ModelLink } from "@/modules/model-link";
import { StateTag } from "@/modules/state-tag";
import { TABLE_FILTER_TEXT } from "@/modules/table-filter";
import { sortTexts } from "@/utilities/string";

import { useBatchPreviewStateParam } from "../batch-preview.hook";

import { STATE_REF_CONFIG } from "./batch-preview-table.constant";

export const useBatchPreviewTableColumnName = <
  Item extends BatchPreviewItem = BatchPreviewItem,
  ModelKey extends BatchPreviewModelKey = BatchPreviewModelKey,
>(
  model: ModelKey
): BatchPreviewTableColumn<Item> => {
  const { t } = useTranslation();

  return {
    key: "name",
    dataIndex: "name",
    title: t("global:name"),
    render: (_name, item) => <ModelLink model={model} item={item} />,
    ...TABLE_FILTER_TEXT,
    onFilter: (value, item) => item.name.includes(value as string),
    defaultSortOrder: "ascend",
    sorter: (a, b) => sortTexts(a.name, b.name),
  };
};

export const useBatchPreviewTableColumnState = <
  Item extends BatchPreviewItem = BatchPreviewItem,
  ModelKey extends BatchPreviewModelKey = BatchPreviewModelKey,
>(
  model: ModelKey,
  items: Item[]
): BatchPreviewTableColumn<Item> => {
  const { t } = useTranslation();

  const stateParam = useBatchPreviewStateParam();
  const { states: stateRefs } = useReferentialStates(STATE_REF_CONFIG[model]);

  const stateFilters = uniqBy(items, "state")
    .map((item) => ({
      value: item.state,
      text: <StateTag type={model} value={item.state} />,
      order: stateRefs.find((s) => s.value === item.state)?.index ?? 0,
    }))
    .sort((a, b) => a.order - b.order);

  return {
    key: "state",
    dataIndex: "state",
    title: t("global:state"),
    render: (state) => (
      <Flex gap="small">
        <StateTag type={model} value={state} />
        {stateParam && state !== stateParam && (
          <Tooltip title={t("global:batchPreview.mismatchState")}>
            <InfoCircleOutlined style={{ cursor: "help" }} />
          </Tooltip>
        )}
      </Flex>
    ),
    ...(stateFilters.length > 1
      ? {
          filters: stateFilters,
          onFilter: (value, item) => item.state === value,
          sorter: (a, b) => sortTexts(a.state, b.state),
        }
      : {}),
  };
};
